<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStaffStore } from '../stores/staffStore'
import type { IStaffDepartment } from '../types/IStaffDepartment'
import type { IStaffEmployee } from '../types/IStaffEmployee'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

interface Props {
  departmentId: string
  departmentName: string
  modelValue: boolean
  employee?: IStaffEmployee | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { smAndDown } = useDisplay()
const staffStore = useStaffStore()
const feedbackStore = useFeedbackStore()
const isColleaguesExpanded = ref(false)
const structureTreeRef = ref<HTMLElement | null>(null)
const hierarchyConnectorPaths = ref<string[]>([])

type TSidebarPerson = Partial<IStaffEmployee> & {
  id: string
  full_name?: string
  position_name?: string
}

const fallbackAvatars = new Map<string, string>()

// Двусторонняя связь с родителем через computed
const isOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

// Отделяем сотрудников данного подразделения
const departmentEmployees = ref<IStaffEmployee[]>([])

async function loadDepartmentEmployees() {
  if (!props.departmentId) {
    departmentEmployees.value = []
    return
  }
  try {
    departmentEmployees.value = await staffStore.fetchEmployeesByDepartment(props.departmentId, 100)
  } catch (error) {
    console.error('❌ Ошибка загрузки сотрудников подразделения:', error)
  }
}

const findDepartmentPath = (
  departments: IStaffDepartment[],
  departmentId: string,
  path: IStaffDepartment[] = [],
): IStaffDepartment[] => {
  for (const department of departments) {
    const nextPath = [...path, department]
    if (String(department.id) === String(departmentId)) return nextPath

    const childPath = findDepartmentPath(department.children ?? [], departmentId, nextPath)
    if (childPath.length) return childPath
  }

  return []
}

const departmentPath = computed(() =>
  findDepartmentPath(staffStore.departments, props.departmentId),
)

watch(() => [props.modelValue, props.departmentId], async ([isOpen, deptId]) => {
  if (isOpen && deptId) {
    isColleaguesExpanded.value = false
    if (!departmentPath.value.length)
      await staffStore.fetchDepartments()
    await loadDepartmentEmployees()
  }
}, { immediate: true })

const sidebarWidth = computed(() =>
  370 + Math.max(departmentPath.value.length - 3, 0) * 28,
)

const selectedDepartment = computed(() =>
  departmentPath.value.at(-1),
)

const leader = computed<TSidebarPerson | null>(() => {
  const manager = selectedDepartment.value?.manager
  if (!manager) return null

  return departmentEmployees.value.find(employee => String(employee.id) === String(manager.id))
    ?? manager
})

const colleagues = computed(() => {
  const selectedEmployeeId = props.employee?.id
  const leaderId = leader.value?.id

  return departmentEmployees.value.filter(employee =>
    employee.status !== 'Уволен'
    && String(employee.id) !== String(selectedEmployeeId)
    && String(employee.id) !== String(leaderId),
  )
})

const getPersonName = (person: TSidebarPerson): string =>
  person.full_name
  || [person.last_name, person.first_name, person.middle_name].filter(Boolean).join(' ')
  || '—'

const getPersonPosition = (person: TSidebarPerson): string =>
  person.position?.name || person.position_name || '—'

const getPersonAvatar = (person: TSidebarPerson): string => {
  if (person.avatar) return person.avatar

  const id = String(person.id)
  if (!fallbackAvatars.has(id)) {
    fallbackAvatars.set(id, getRandomAvatar())
  }

  return fallbackAvatars.get(id)!
}

const updateHierarchyConnector = () => {
  const tree = structureTreeRef.value
  if (!tree) return

  const rows = Array.from(tree.querySelectorAll<HTMLElement>('.tree-item'))
  if (rows.length < 2) {
    hierarchyConnectorPaths.value = []
    return
  }

  const treeRect = tree.getBoundingClientRect()
  const cornerRadius = 6
  const armLength = 20
  const paths: string[] = []

  /* Уголок стоит под левым краем текста родителя, плечи равны — как в макете */
  rows.forEach((row, index) => {
    if (index === 0) return

    const parentContent = rows[index - 1].querySelector<HTMLElement>('.tree-content')
    const content = row.querySelector<HTMLElement>('.tree-content')
    if (!parentContent || !content) return

    const parentRect = parentContent.getBoundingClientRect()
    const contentRect = content.getBoundingClientRect()

    const startX = parentRect.left - treeRect.left
    const endX = startX + armLength
    const endY = contentRect.top - treeRect.top + Math.min(contentRect.height / 2, 11)
    const startY = endY - armLength

    paths.push([
      `M ${startX} ${startY}`,
      `V ${endY - cornerRadius}`,
      `A ${cornerRadius} ${cornerRadius} 0 0 0 ${startX + cornerRadius} ${endY}`,
      `H ${endX}`,
    ].join(' '))
  })

  hierarchyConnectorPaths.value = paths
}

watch(
  () => [props.modelValue, departmentPath.value.map(department => department.id).join(':')],
  async () => {
    await nextTick()
    updateHierarchyConnector()
  },
  { immediate: true },
)

useResizeObserver(structureTreeRef, updateHierarchyConnector)
</script>

<template>
  <VDialog
    v-model="isOpen"
    persistent
    class="department-dialog"
    :width="smAndDown ? '100%' : `min(50vw, ${sidebarWidth}px)`"
  >
    <VCard class="d-flex flex-column h-100 w-100 bg-surface" style="border-radius: 0 !important; margin-left: auto !important; height: 100% !important; min-height: 100% !important;">
      <!-- Кастомная шапка вместо VCardTitle -->
      <div class="d-flex align-center justify-space-between pa-4 border-b">
        <span class="text-subtitle-1 font-weight-bold text-truncate text-high-emphasis">
          Место в структуре
        </span>
        <CustomCloseButton :close-action="() => isOpen = false" />
      </div>

      <!-- Основной контент сайдбара -->
      <div class="pa-5 flex-grow-1 overflow-y-auto d-flex flex-column gap-6">
        <!-- Блок выбранного сотрудника -->
        <div v-if="employee" class="d-flex align-center gap-3">
          <VAvatar :image="getPersonAvatar(employee)" size="48" />
          <div class="d-flex flex-column">
            <span class="text-body-1 font-weight-bold text-high-emphasis leading-tight">
              {{ getPersonName(employee) }}
            </span>
            <span class="text-caption text-medium-emphasis mt-0.5">
              {{ getPersonPosition(employee) }}
            </span>
          </div>
        </div>

        <!-- Дерево структуры -->
        <div ref="structureTreeRef" class="structure-tree d-flex flex-column">
          <svg
            v-if="hierarchyConnectorPaths.length"
            class="structure-tree__connectors"
            aria-hidden="true"
          >
            <path
              v-for="(connector, connectorIndex) in hierarchyConnectorPaths"
              :key="connectorIndex"
              :d="connector"
            />
          </svg>

          <div
            v-for="(department, index) in departmentPath"
            :key="department.id"
            class="tree-item d-flex align-start ga-2"
            :style="{ paddingInlineStart: index ? `${index * 28}px` : undefined }"
          >
            <div class="tree-column d-flex align-center justify-center">
              <VIcon v-if="index === 0" icon="ri-building-line" size="20" class="text-primary" />
            </div>
            <div class="tree-content d-flex flex-column">
              <span
                class="text-body-2 font-weight-semibold"
                :class="index === departmentPath.length - 1 ? 'text-primary' : 'text-high-emphasis'"
              >
                {{ department.name || department.short_name }}
              </span>
            </div>
          </div>

          <div v-if="!departmentPath.length" class="text-body-2 font-weight-semibold text-primary">
            {{ departmentName || '—' }}
          </div>
        </div>

        <!-- Раздел: Руководитель -->
        <div v-if="leader">
            <div class="text-caption font-weight-bold text-disabled text-uppercase mb-2" style="letter-spacing: 0.8px;">
              Руководитель
            </div>
            <div class="d-flex align-center gap-3 py-1">
              <VAvatar :image="getPersonAvatar(leader)" size="40" />
              <div class="d-flex flex-column">
                <span class="text-body-2 font-weight-semibold text-high-emphasis">
                  {{ getPersonName(leader) }}
                </span>
                <span class="text-caption text-medium-emphasis mt-0.5">
                  {{ getPersonPosition(leader) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Раздел: Коллеги -->
          <div v-if="colleagues.length > 0" class="d-flex flex-column">
            <!-- Заголовок с переключателем и счетчиком -->
            <div
              class="d-flex align-center justify-space-between cursor-pointer py-1"
              style="user-select: none;"
              @click="isColleaguesExpanded = !isColleaguesExpanded"
            >
              <div class="d-flex align-center gap-2">
                <span class="text-caption font-weight-bold text-disabled text-uppercase" style="letter-spacing: 0.8px;">
                  Коллеги
                </span>
                <VIcon
                  :icon="isColleaguesExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                  size="16"
                  class="text-disabled"
                />
              </div>
              <span class="px-2 py-0.5 rounded-pill text-caption font-weight-semibold" style="background-color: rgba(var(--v-theme-on-surface), 0.06); color: rgba(var(--v-theme-on-surface), 0.6); min-width: 20px; text-align: center;">
                {{ colleagues.length }}
              </span>
            </div>

            <!-- Список коллег -->
            <div v-if="isColleaguesExpanded" class="d-flex flex-column gap-3 mt-3">
              <div v-for="colleague in colleagues" :key="colleague.id" class="d-flex align-center gap-3 py-1">
                <VAvatar :image="getPersonAvatar(colleague)" size="36" />
                <div class="d-flex flex-column">
                  <span class="text-body-2 font-weight-semibold text-high-emphasis">
                    {{ getPersonName(colleague) }}
                  </span>
                  <span class="text-caption text-medium-emphasis mt-0.5">
                    {{ getPersonPosition(colleague) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.department-dialog :deep(.v-overlay__content) {
  position: fixed !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  margin: 0 !important;
  max-width: none !important;
  max-height: none !important;
  height: 100% !important;
}

// Верстка дерева с L-образными линиями связи
.structure-tree {
  position: relative;

  .tree-item {
    position: relative;
    z-index: 1;
    padding-bottom: 12px;

    &:last-child {
      padding-bottom: 0;
    }
  }

  /* Высота колонки равна line-height первой строки — иконка встаёт по центру заголовка */
  .tree-column {
    flex: 0 0 24px;
    inline-size: 24px;
    block-size: 22px;
  }
}

.structure-tree__connectors {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;

  path {
    fill: none;
    stroke: rgb(var(--v-theme-primary));
    stroke-width: 1.5;
  }
}
</style>
